<script setup lang="ts">
import { ref, computed, watch, reactive } from "vue";
import VText from "@magnit/core/src/components/VText/VText.vue";
import VInput from "@magnit/core/src/components/VInput/VInput.vue";
import VRangeSlider from "@magnit/core/src/components/VRangeSlider/VRangeSlider.vue";
import { useDebounceFn } from "@vueuse/core";
import type { IRangeFilterValue, IRangeFilterProps, IMinMax } from "../types";

const props = defineProps<IRangeFilterProps>();
const modelValue = defineModel<IRangeFilterValue>({
  default: { min: 0, max: 0 },
});

const inputsFocused = reactive({ min: false, max: false });
const inputsCurrentValues = reactive({ min: "", max: "" });
const decimalPlaces = ref<number | null>(null);

const minInputRef = ref<HTMLInputElement | null>(null);
const maxInputRef = ref<HTMLInputElement | null>(null);

const formatDecimals = (num: number) =>
  decimalPlaces.value ? num.toFixed(decimalPlaces.value) : num.toString();

const currentMin = ref(modelValue.value.min || props.filter.minValue);
const currentMax = ref(modelValue.value.max || props.filter.maxValue);

const filterChanged = computed(
  () =>
    props.filter.minValue !== currentMin.value ||
    props.filter.maxValue !== currentMax.value,
);

const step = computed(() => {
  const DEFAULT_STEP = 1;
  return props.filter.step ?? DEFAULT_STEP;
});

const onChange = (value: IRangeFilterValue) => {
  currentMin.value = value.min;
  currentMax.value = value.max;
};

const onDrag = (value: IRangeFilterValue) => {
  inputsCurrentValues.min = formatDecimals(value.min);
  inputsCurrentValues.max = formatDecimals(value.max);
};

const onInputUpdate = (minmax: IMinMax) => {
  const currentRawValue = Number(inputsCurrentValues[minmax]);
  const newValue = Number(
    minmax === "min"
      ? currentRawValue > Number(currentMax.value)
        ? currentMax.value
        : currentRawValue < props.filter.minValue
          ? props.filter.minValue
          : currentRawValue
      : currentRawValue < Number(currentMin.value)
        ? currentMin.value
        : currentRawValue > props.filter.maxValue
          ? props.filter.maxValue
          : currentRawValue,
  );
  if (minmax === "min") {
    currentMin.value = newValue;
    inputsCurrentValues.min = formatDecimals(newValue);
  } else {
    maxInputRef?.value?.blur();
    currentMax.value = newValue;
    inputsCurrentValues.max = formatDecimals(newValue);
  }
};

const onEscape = (e: Event, minmax: IMinMax) => {
  inputsCurrentValues[minmax] = formatDecimals(
    minmax === "min" ? currentMin.value : currentMax.value,
  );
  const inputElement = e.target as HTMLInputElement;
  inputElement.blur();
};

const onInputChange = (value: string, minmax: IMinMax) => {
  if (inputsFocused[minmax]) {
    inputsCurrentValues[minmax] = value;
  }
};

const minPlaceholder = computed(() => formatDecimals(props.filter.minValue));
const maxPlaceholder = computed(() => formatDecimals(props.filter.maxValue));

const onClear = () => {
  inputsCurrentValues.min = formatDecimals(props.filter.minValue);
  inputsCurrentValues.max = formatDecimals(props.filter.maxValue);
  currentMax.value = props.filter.maxValue;
  currentMin.value = props.filter.minValue;
  modelValue.value = { min: props.filter.minValue, max: props.filter.maxValue };
};

const onFocus = (minmax: IMinMax) => {
  if (minmax === "min") {
    inputsFocused.max = false;
    maxInputRef?.value?.blur();
  } else {
    inputsFocused.min = false;
    minInputRef?.value?.blur();
  }
  inputsFocused[minmax] = true;
};

const debouncedUpdateModelValue = useDebounceFn((value: IRangeFilterValue) => {
  modelValue.value = value;
}, 300);

watch(
  () => props.filter.step,
  (next) => {
    const decimalPart = next && next % 1;
    decimalPlaces.value = decimalPart
      ? decimalPart.toString().split(".")[1].length
      : null;
  },
  { immediate: true },
);

watch(
  () => [currentMin.value, currentMax.value],
  (next) => {
    debouncedUpdateModelValue({
      min: +next[0].toFixed(2),
      max: +next[1].toFixed(2),
    });
  },
);
watch(
  () => props.filter.minValue,
  () => {
    inputsCurrentValues.min = formatDecimals(props.filter.minValue);
  },
  { immediate: true },
);
watch(
  () => props.filter.maxValue,
  () => {
    inputsCurrentValues.max = formatDecimals(props.filter.maxValue);
  },
  { immediate: true },
);
watch(
  () => modelValue.value,
  (next) => {
    const min = next && next.min ? next.min : props.filter.minValue;
    const max = next && next.max ? next.max : props.filter.maxValue;
    currentMin.value = min;
    currentMax.value = max;
    inputsCurrentValues.min = formatDecimals(min);
    inputsCurrentValues.max = formatDecimals(max);
  },
  { immediate: true },
);
</script>

<template>
  <div class="filter-range">
    <div class="filter-range__title">
      <VText class="filter-range__title_name" font="title-medium">
        {{ filter.title }}
      </VText>
      <VText v-if="filterChanged" font="body-large-accent" @click="onClear">
        Сбросить
      </VText>
    </div>
    <div class="filter-range__inputs">
      <VInput
        ref="minInputRef"
        :model-value="inputsCurrentValues.min"
        :placeholder="minPlaceholder"
        :clearable="false"
        prefix="От"
        type="number"
        @focus="onFocus('min')"
        @update:model-value="(value: string) => onInputChange(value, 'min')"
        @blur="onInputUpdate('min')"
        @keydown.enter="onInputUpdate('min')"
        @keydown.tab="onInputUpdate('min')"
        @keydown.esc="(e: Event) => onEscape(e, 'min')"
      />
      <VInput
        ref="maxInputRef"
        :model-value="inputsCurrentValues.max"
        :placeholder="maxPlaceholder"
        :clearable="false"
        prefix="До"
        type="number"
        @focus="onFocus('max')"
        @update:model-value="(value: string) => onInputChange(value, 'max')"
        @blur="onInputUpdate('max')"
        @keydown.enter="onInputUpdate('max')"
        @keydown.tab="onInputUpdate('max')"
        @keydown.esc="(e: Event) => onEscape(e, 'max')"
      />
    </div>
    <VRangeSlider
      :min="filter.minValue"
      :max="filter.maxValue"
      :current-min="currentMin"
      :current-max="currentMax"
      :step="step"
      @drag="onDrag"
      @change="onChange"
    />
  </div>
</template>

<style lang="postcss" scoped>
.filter-range {
  &__title {
    padding: var(--pl-unit-x2) var(--pl-unit-x2) var(--pl-unit-x2) 0;
    margin-top: var(--pl-unit-x4);
    display: flex;
    align-items: baseline;
    gap: var(--pl-unit-x2);

    &_name + * {
      cursor: pointer;
    }
  }

  &__inputs {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--pl-unit-x4);
    position: relative;

    &::before {
      position: absolute;
      content: "";
      width: var(--pl-unit-x2);
      height: 2px;
      border-radius: var(--pl-unit-x1);
      background-color: var(--pl-surface-subdued-default);
      inset: 0;
      margin: auto;
    }
  }
}
</style>
